import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Minus, Plus, ArrowLeft, ArrowRight, PackageX } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { ProductCard } from "@/components/Featured";
import Reveal from "@/components/Reveal";
import api from "@/lib/api";

export default function ProductDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { add } = useCart();
  const [p, setP] = useState(null);
  const [related, setRelated] = useState([]);
  const [qty, setQty] = useState(100);
  const [spec, setSpec] = useState("");
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setP(null);
    setNotFound(false);
    setQty(100);
    setSpec("");
    api
      .get(`/products/${slug}`)
      .then((r) => {
        setP(r.data);
        return Promise.all([r.data, api.get("/products")]);
      })
      .then(([prod, r]) =>
        setRelated(
          (r.data || [])
            .filter((x) => x.category === prod.category && x.slug !== slug)
            .slice(0, 3)
        )
      )
      .catch(() => setNotFound(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const addAnd = (go) => {
    add(p, qty, spec.trim());
    if (go) {
      setTimeout(() => navigate("/quote"), 350);
    }
  };

  if (notFound) {
    return (
      <main className="pt-40 pb-24 min-h-[70vh] text-center" data-testid="product-not-found">
        <PackageX size={40} className="mx-auto text-mintsoft/50 mb-4" />
        <h1 className="font-display text-2xl text-kraft mb-2">This box slipped out of stock</h1>
        <Link to="/shop" data-testid="not-found-back" className="text-mint hover:underline font-mono text-xs tracking-[0.2em] uppercase">
          ← Back to the shop
        </Link>
      </main>
    );
  }

  if (!p) {
    return <main className="pt-40 pb-24 min-h-[70vh]" data-testid="product-loading"><p className="font-mono text-xs tracking-[0.3em] uppercase text-mintsoft/70 text-center">FOLDING…</p></main>;
  }

  const specs = Object.entries(p.specs || {});

  return (
    <main className="pt-28 pb-24 min-h-screen" data-testid="product-detail-page">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <button
          onClick={() => navigate(-1)}
          data-testid="product-back-btn"
          className="font-mono text-[11px] tracking-[0.2em] uppercase text-kraft/60 hover:text-mint transition-colors flex items-center gap-2 mb-8"
        >
          <ArrowLeft size={14} /> Back
        </button>

        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-start">
          <Reveal>
            <div className="relative overflow-hidden rounded-2xl border border-mint/15 clip-corner" data-testid="product-image-frame">
              <img src={p.image} alt={p.name} className="w-full aspect-square object-cover" />
              <div
                className="absolute inset-0 pointer-events-none"
                style={{ background: "radial-gradient(ellipse at 50% 0%, rgba(244,237,226,0.16), transparent 60%)" }}
              />
              <span className="absolute top-4 left-4 font-mono text-[10px] tracking-[0.25em] uppercase bg-forest/70 backdrop-blur-sm border border-mint/20 text-mintsoft px-3 py-1.5 rounded-full">
                {p.category} box
              </span>
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <div>
              <p className="font-mono text-[11px] tracking-[0.3em] uppercase text-mintsoft/90 mb-3">{p.tagline}</p>
              <h1 className="font-display font-semibold tracking-tight text-4xl sm:text-5xl text-kraft leading-[1.02] mb-6" data-testid="product-name">
                {p.name}
              </h1>
              <p className="text-kraft/70 leading-relaxed mb-8">{p.description}</p>

              <div className="rounded-2xl border border-mint/15 bg-moss/30 divide-y divide-mint/10 mb-8" data-testid="product-specs">
                {specs.map(([k, v]) => (
                  <div key={k} className="flex justify-between gap-6 px-5 py-3.5">
                    <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-mintsoft/70 pt-0.5">{k}</span>
                    <span className="font-mono text-xs text-kraft text-right">{v}</span>
                  </div>
                ))}
              </div>

              <div className="space-y-5">
                <div>
                  <label className="font-mono text-[10px] tracking-[0.2em] uppercase text-mintsoft/80 block mb-2">
                    Quantity (boxes)
                  </label>
                  <div className="flex items-center gap-4">
                    <div className="flex items-center border border-mint/25 rounded-full" data-testid="product-qty-stepper">
                      <button onClick={() => setQty((q) => Math.max(1, q - 50))} data-testid="product-qty-minus" className="px-4 h-11 grid place-items-center text-kraft hover:text-mint">
                        <Minus size={15} />
                      </button>
                      <input
                        type="number"
                        value={qty}
                        min="1"
                        onChange={(e) => setQty(Math.max(1, +e.target.value || 1))}
                        data-testid="product-qty-input"
                        className="w-20 bg-transparent text-center font-mono text-sm text-kraft focus:outline-none"
                      />
                      <button onClick={() => setQty((q) => q + 50)} data-testid="product-qty-plus" className="px-4 h-11 grid place-items-center text-kraft hover:text-mint">
                        <Plus size={15} />
                      </button>
                    </div>
                    <span className="font-mono text-[10px] text-kraft/45 uppercase tracking-[0.15em]">±50 steps</span>
                  </div>
                </div>

                <div>
                  <label className="font-mono text-[10px] tracking-[0.2em] uppercase text-mintsoft/80 block mb-2">
                    Custom size or print (optional)
                  </label>
                  <input
                    value={spec}
                    onChange={(e) => setSpec(e.target.value)}
                    placeholder="e.g. 24 × 18 × 12 cm, logo in gold foil"
                    data-testid="product-spec-input"
                    className="w-full h-11 rounded-full bg-moss/40 border border-mint/25 px-5 text-sm text-kraft placeholder:text-kraft/35 focus:outline-none focus:border-mint"
                  />
                </div>

                <div className="flex flex-wrap gap-3 pt-2">
                  <button
                    onClick={() => addAnd(false)}
                    data-testid="add-to-quote-btn"
                    className="inline-flex items-center gap-2 rounded-full bg-mint text-forest font-semibold px-7 h-12 hover:bg-kraft transition-colors"
                  >
                    Add to quote cart
                  </button>
                  <button
                    onClick={() => addAnd(true)}
                    data-testid="request-quote-now-btn"
                    className="inline-flex items-center gap-2 rounded-full border border-mint/40 text-kraft px-7 h-12 hover:border-mint hover:text-mint transition-colors"
                  >
                    Request quote now
                    <ArrowRight size={16} />
                  </button>
                </div>
                <p className="font-mono text-[10px] tracking-[0.18em] uppercase text-kraft/40">
                  No prices listed — every box quoted on size, print & volume
                </p>
              </div>
            </div>
          </Reveal>
        </div>

        {related.length > 0 && (
          <section className="mt-24" data-testid="related-products">
            <Reveal>
              <h2 className="font-display font-semibold text-2xl sm:text-3xl text-kraft mb-8">
                Same family, other folds
              </h2>
            </Reveal>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {related.map((r, i) => (
                <Reveal key={r.id} delay={i * 0.07} className="h-full">
                  <ProductCard p={r} index={i} />
                </Reveal>
              ))}
            </div>
          </section>
        )}
      </div>
    </main>
  );
}
